import { useRef, useState, useEffect } from "react";
import { Upload, CheckCircle2, FileText, X, AlertCircle } from "lucide-react";
import { api, getApiError } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "mortgage_doc", label: "Mortgage statement" },
  { key: "deed",         label: "Deed / title" },
  { key: "id",           label: "Government ID" },
  { key: "court_filing", label: "Court papers" },
  { key: "sheriff_notice", label: "Sheriff-sale notice" },
  { key: "other",        label: "Other" },
];

const RECOMMENDED = new Set(["mortgage_doc", "deed", "id", "court_filing"]);

/**
 * Big, prominent drag-and-drop document upload panel for the Member Portal.
 * Categorized checklist so new members know exactly what to send to the trustee.
 * Fully touch/tablet friendly.
 */
export default function DocumentUploadPanel({ files = [], onUploaded, spotlight = false }) {
  const [selectedCategory, setSelectedCategory] = useState("mortgage_doc");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef();

  const uploadedByCategory = files.reduce((acc, f) => {
    acc[f.category] = (acc[f.category] || 0) + 1;
    return acc;
  }, {});
  const recommendedRemaining = [...RECOMMENDED].filter((k) => !uploadedByCategory[k]).length;
  const totalDone = [...RECOMMENDED].length - recommendedRemaining;

  const handleFiles = async (fileList) => {
    const chosen = Array.from(fileList || []);
    if (chosen.length === 0) return;
    setUploading(true);
    setProgress(0);
    let ok = 0;
    for (let i = 0; i < chosen.length; i++) {
      const f = chosen[i];
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", selectedCategory);
        await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) {
              const pct = Math.round(((i * 100) + (evt.loaded * 100 / evt.total)) / chosen.length);
              setProgress(pct);
            }
          },
        });
        ok += 1;
      } catch (err) {
        toast.error(`${f.name}: ${getApiError(err)}`);
      }
    }
    setUploading(false);
    setProgress(0);
    if (ok > 0) toast.success(`${ok} document${ok > 1 ? "s" : ""} sent to your trustee`);
    if (inputRef.current) inputRef.current.value = "";
    onUploaded?.();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };
  const onDrag = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  // Prompt keyboard on iOS after category change if drag not available
  useEffect(() => {}, [selectedCategory]);

  return (
    <section
      className={`border ${spotlight ? "border-stone-900" : "border-stone-200"} bg-white`}
      data-testid="member-upload-panel"
    >
      <header className="px-6 md:px-8 py-6 border-b border-stone-200 flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
        <div className="flex items-start gap-4">
          <div className="h-11 w-11 border border-stone-900 flex items-center justify-center flex-shrink-0">
            <Upload className="h-5 w-5" />
          </div>
          <div>
            <div className="overline">§ Trustee Document Vault</div>
            <h2 className="font-serif font-light text-2xl md:text-3xl leading-tight mt-1">
              Upload documents to your trustee
            </h2>
            <p className="text-sm text-stone-600 font-light mt-2 max-w-lg">
              Send your intake documents directly to a Guild trustee. Files are encrypted, stored in the
              private vault, and only visible to you and your assigned trustee.
            </p>
          </div>
        </div>
        <div className="w-full md:w-auto md:text-right shrink-0">
          <div className="overline mb-2">§ Progress</div>
          <div className="font-serif text-3xl md:text-4xl text-stone-900 leading-none">
            {totalDone} <span className="text-stone-400">/ {RECOMMENDED.size}</span>
          </div>
          <div className="caption text-stone-500 mt-1">Recommended docs sent</div>
        </div>
      </header>

      <div className="p-6 md:p-8 grid lg:grid-cols-5 gap-6 md:gap-8">
        {/* Category picker */}
        <div className="lg:col-span-2 space-y-3">
          <div className="label-luxury">1 · Choose category</div>
          <div role="radiogroup" aria-label="Document category" className="border border-stone-300 divide-y divide-stone-200" data-testid="member-upload-category-group">
            {CATEGORIES.map((c) => {
              const done = (uploadedByCategory[c.key] || 0) > 0;
              const recommended = RECOMMENDED.has(c.key);
              const selected = selectedCategory === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setSelectedCategory(c.key)}
                  className={`w-full text-left px-4 py-3.5 flex items-center gap-3 transition-colors ${
                    selected ? "bg-stone-900 text-stone-50" : "bg-white hover:bg-stone-50 text-stone-800"
                  }`}
                  data-testid={`member-upload-cat-${c.key}`}
                >
                  <span className={`h-5 w-5 border flex items-center justify-center flex-shrink-0 ${
                    selected ? "border-stone-50 bg-stone-50 text-stone-900" : "border-stone-400 bg-white text-stone-900"
                  }`}>
                    {done && <CheckCircle2 className="h-3.5 w-3.5" />}
                  </span>
                  <span className="flex-1 text-sm">{c.label}</span>
                  {recommended && !done && (
                    <span className={`font-mono text-[9px] uppercase tracking-[0.18em] ${selected ? "text-stone-300" : "text-stone-500"}`}>
                      Recommended
                    </span>
                  )}
                  {done && (
                    <span className={`font-mono text-[9px] uppercase tracking-[0.18em] ${selected ? "text-stone-300" : "text-stone-500"}`}>
                      {uploadedByCategory[c.key]} on file
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Drop zone */}
        <div className="lg:col-span-3">
          <div className="label-luxury">2 · Drop or choose files</div>
          <div
            onDragOver={onDrag}
            onDragEnter={onDrag}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => !uploading && inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !uploading) inputRef.current?.click(); }}
            className={`border-2 border-dashed transition-colors cursor-pointer min-h-[220px] flex flex-col items-center justify-center text-center p-8 ${
              dragging
                ? "border-stone-900 bg-stone-100"
                : "border-stone-300 hover:border-stone-900 hover:bg-stone-50"
            } ${uploading ? "opacity-70 pointer-events-none" : ""}`}
            data-testid="member-upload-dropzone"
          >
            {uploading ? (
              <>
                <div className="overline mb-3">§ Uploading</div>
                <div className="font-serif text-3xl text-stone-900">{progress}%</div>
                <div className="w-full max-w-xs mt-4 h-px bg-stone-200">
                  <div className="h-full bg-stone-900 transition-all" style={{ width: `${progress}%` }} />
                </div>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 mb-3 text-stone-500" />
                <p className="font-serif text-lg md:text-xl text-stone-900">
                  Drop files here, or <span className="underline">tap to browse</span>
                </p>
                <p className="text-xs text-stone-500 font-light mt-2 max-w-xs">
                  PDF, JPG, PNG, DOC, DOCX up to 25 MB each. Multiple files supported.
                </p>
              </>
            )}
            <input
              ref={inputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => handleFiles(e.target.files)}
              accept="image/*,application/pdf,.doc,.docx,.txt,.rtf,.heic,.heif"
              data-testid="member-upload-input"
            />
          </div>
          {recommendedRemaining > 0 && (
            <div className="mt-4 flex items-start gap-3 text-xs text-stone-700 font-light" data-testid="member-upload-remaining">
              <AlertCircle className="h-4 w-4 mt-0.5 text-stone-900 flex-shrink-0" />
              <p>
                <strong className="font-medium">{recommendedRemaining}</strong>{" "}
                recommended documents still needed. Your trustee cannot begin the ledger audit without them.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="border-t border-stone-200" data-testid="member-upload-list">
          <div className="px-6 md:px-8 py-4 flex items-center justify-between">
            <div className="overline">§ On File · {files.length}</div>
          </div>
          <ul className="divide-y divide-stone-200 border-t border-stone-200">
            {files.slice().reverse().map((f) => (
              <li
                key={f.file_id}
                className="px-6 md:px-8 py-4 flex items-center gap-4"
                data-testid={`member-file-${f.file_id}`}
              >
                <FileText className="h-4 w-4 text-stone-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-stone-900 truncate">{f.original_filename}</div>
                  <div className="caption text-stone-500 mt-0.5">
                    {(CATEGORIES.find((c) => c.key === f.category)?.label || f.category)}
                    {" · "}
                    {new Date(f.uploaded_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                </div>
                <CheckCircle2 className="h-4 w-4 text-stone-900" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
